/**
 * Excel Processing API Service
 */

import { api } from './api';
import type {
  UploadRequestBody,
  UploadResponse,
  ParseExcelResponse,
  ParseProgressResponse,
} from '../types/api';

export const excelApi = {
  /**
   * Get presigned URL for Excel file upload
   */
  getUploadUrl: async (request: UploadRequestBody): Promise<UploadResponse> => {
    return api.post<UploadResponse>('/excel/upload', request);
  },

  /**
   * Upload file to S3 using presigned URL
   */
  uploadFile: async (presignedUrl: string, file: File): Promise<void> => {
    const response = await fetch(presignedUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type,
      },
    });

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.statusText}`);
    }
  },

  /**
   * Get parsing job status
   */
  getParseStatus: async (jobId: string): Promise<ParseProgressResponse> => {
    return api.get<ParseProgressResponse>(`/admin/excel/parse-status/${jobId}`);
  },

  /**
   * Fetch parsed Excel results from presigned S3 URL
   */
  fetchParseResult: async (url: string): Promise<ParseExcelResponse> => {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch parse results: ${response.statusText}`);
    }
    return response.json();
  },

  /**
   * Poll for job completion (helper method)
   */
  pollForCompletion: async (
    jobId: string,
    options: {
      interval?: number;
      maxAttempts?: number;
      onProgress?: (status: ParseProgressResponse) => void;
    } = {}
  ): Promise<ParseProgressResponse> => {
    const { interval = 2000, maxAttempts = 60, onProgress } = options;
    let attempts = 0;

    return new Promise((resolve, reject) => {
      const poll = async () => {
        if (attempts >= maxAttempts) {
          reject(new Error('Polling timeout: max attempts reached'));
          return;
        }

        attempts++;

        try {
          const status = await excelApi.getParseStatus(jobId);

          if (onProgress) {
            onProgress(status);
          }

          if (status.status === 'completed') {
            resolve(status);
          } else if (status.status === 'failed') {
            reject(new Error(status.error_message || 'Parsing failed'));
          } else {
            // Still processing, poll again
            setTimeout(poll, interval);
          }
        } catch (error) {
          reject(error);
        }
      };

      poll();
    });
  },
};
